/**
 * Reads the account's billing state.
 *
 * Everything here is READ-ONLY. Entitlements are written exclusively by the
 * verified `paddle-webhook` edge function using the service role; the browser
 * has no insert/update grant on any billing table, so a manipulated client
 * cannot grant itself access.
 */
import { supabase } from "@/integrations/supabase/client";
import type { BillingInterval, SubscriptionStatus, Tier } from "@/lib/billingConfig";

export type SubscriptionRecord = {
  id: string;
  paddle_subscription_id: string;
  tier: Tier;
  bill_interval: BillingInterval;
  status: SubscriptionStatus;
  current_period_end: string | null;
  scheduled_cancel_at: string | null;
  canceled_at: string | null;
  past_due_since: string | null;
  management_url: string | null;
  cancel_url: string | null;
  updated_at: string;
};

export type EntitlementRecord = {
  id: string;
  plan: "free" | "beta" | "paid";
  tier: Tier | null;
  source: "manual" | "beta" | "paddle";
  active: boolean;
  grace_until: string | null;
  expires_at: string | null;
};

export type BillingState = {
  subscription: SubscriptionRecord | null;
  entitlement: EntitlementRecord | null;
  /** Paid-only actions are permitted right now. */
  canUsePaidFeatures: boolean;
  /** Show a calm "your plan has lapsed, your data is safe" banner. */
  isReadOnly: boolean;
  /** True while inside a past-due or cancellation grace window. */
  inGrace: boolean;
};

function graceIsOpen(graceUntil: string | null, now: Date): boolean {
  if (!graceUntil) return false;
  const t = new Date(graceUntil).getTime();
  return Number.isFinite(t) && t > now.getTime();
}

/** Derive the effective billing state from the stored records. */
export function deriveBillingState(
  subscription: SubscriptionRecord | null,
  entitlement: EntitlementRecord | null,
  now: Date = new Date(),
): BillingState {
  const inGrace = graceIsOpen(entitlement?.grace_until ?? null, now);
  const notExpired =
    !entitlement?.expires_at || new Date(entitlement.expires_at).getTime() > now.getTime();

  const canUsePaidFeatures =
    (!!entitlement?.active && entitlement.plan !== "free" && notExpired) || inGrace;

  // A lapsed paid account is read-only, never deleted and never locked out.
  const isReadOnly = !canUsePaidFeatures && entitlement?.source === "paddle";

  return { subscription, entitlement, canUsePaidFeatures, isReadOnly, inGrace };
}

export async function fetchBillingState(userId: string): Promise<BillingState> {
  const [subRes, entRes] = await Promise.all([
    supabase
      .from("billing_subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("account_entitlements")
      .select("id, plan, tier, source, active, grace_until, expires_at")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
  ]);

  const subscription = (subRes.data as SubscriptionRecord | null) ?? null;
  const entitlement = (entRes.data as EntitlementRecord | null) ?? null;
  return deriveBillingState(subscription, entitlement);
}

/** Human-readable status for the billing settings page. */
export const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: "Active",
  trialing: "Trial",
  past_due: "Payment failed",
  paused: "Paused",
  canceled: "Cancelled",
};

export function formatRenewalDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
